import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import type { DashboardRecent, DashboardSummary } from '../services/types';
import { formatDate, formatMoney } from '../utils/format';

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recent, setRecent] = useState<DashboardRecent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [sRes, rRes] = await Promise.all([
        api.getDashboardSummary(),
        api.getDashboardRecent(8),
      ]);
      if (sRes.data) setSummary(sRes.data);
      else setError(sRes.error?.message ?? 'Ошибка загрузки');
      if (rRes.data) setRecent(rRes.data);
    })();
  }, []);

  if (error) {
    return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  }

  if (!summary) {
    return <p style={{ color: 'var(--text-muted)' }}>Загрузка dashboard…</p>;
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Dashboard</h1>

      <div className="grid-stats" style={{ marginBottom: '1.5rem' }}>
        <StatCard label="Пользователей" value={summary.total_users} />
        <StatCard label="Активных подписок" value={summary.active_subscriptions} />
        <StatCard label="Истёкших подписок" value={summary.expired_subscriptions} />
        <StatCard label="Без подписки" value={summary.no_subscription} />
        <StatCard label="VPN ключей" value={summary.active_vpn_keys} />
        <StatCard label="Серверов (enabled)" value={summary.enabled_servers} />
        <StatCard label="Premium / Default" value={`${summary.premium_users} / ${summary.default_users}`} />
        <StatCard label="Доход сегодня" value={formatMoney(summary.revenue.today)} />
        <StatCard label="Доход за месяц" value={formatMoney(summary.revenue.month)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>По tier</h3>
          {summary.tier_distribution.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Нет данных</p>
          ) : (
            summary.tier_distribution.map((t) => (
              <div key={t.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>{t.label}</span>
                <strong>{t.count}</strong>
              </div>
            ))
          )}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>По регионам (VPN)</h3>
          {summary.region_distribution.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Нет данных</p>
          ) : (
            summary.region_distribution.map((r) => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>{r.label.toUpperCase()}</span>
                <strong>{r.count}</strong>
              </div>
            ))
          )}
        </div>
      </div>

      {recent && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <RecentBlock title="Последние платежи">
            {recent.payments.map((p) => (
              <div key={p.id} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <Link to={`/users/${p.user_id}`}>{p.id.slice(0, 20)}…</Link>
                {' · '}
                {formatMoney(p.amount)} · {p.status} · {formatDate(p.created_at)}
              </div>
            ))}
          </RecentBlock>
          <RecentBlock title="Последние VPN ключи">
            {recent.vpn_keys.map((k) => (
              <div key={k.id} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <Link to={`/users/${k.user_id}`}>
                  {k.server_country.toUpperCase()} #{k.client_id}
                </Link>
                {' · '}
                {formatDate(k.created_at)}
              </div>
            ))}
          </RecentBlock>
          <RecentBlock title="Последние промокоды">
            {recent.promos.map((p) => (
              <div key={`${p.code}-${p.user_id}`} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <strong>{p.code}</strong> ·{' '}
                <Link to={`/users/${p.user_id}`}>user {p.user_id}</Link> · {formatDate(p.used_at)}
              </div>
            ))}
          </RecentBlock>
          <RecentBlock title="Последние инвайты">
            {recent.invites.map((i) => (
              <div key={i.code} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <strong>{i.code}</strong> · owner{' '}
                <Link to={`/users/${i.owner_id}`}>{i.owner_id}</Link> · {i.status}
              </div>
            ))}
          </RecentBlock>
        </div>
      )}
    </div>
  );
}

function RecentBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0, fontSize: '1rem' }}>{title}</h3>
      {children}
    </div>
  );
}
